<template>
  <div id="results">
    <h1 class="h2">Recommendations for your digital evolution</h1>
    <p>
      One of the most important aspects of your digital evolution is recognizing that the journey will never be over. There will always be opportunities to grow, innovate, optimize, and deliver better experiences. Just by evaluating your situation, you’ve taken a step toward a new phase of modernization.
    </p>
    <p>
      Because you indicated interest in {{ challengeOutput }}, let’s begin to
      discuss the best ways to solve these and other challenges.
    </p>
    <img
      class="horizontal-rule"
      src="../../assets/page-12/solution-builder-page-12-dividers-01.svg"
      alt="checkmark"
    />
    <h3 class="text-blue">Here’s where to start</h3>
    <p>
      Based on the information you provided, we’ve identified a few recommendations and examples of our solution capabilities that will make a big difference at your 
      {{ industryContent[currentIndustry][6] }}:
    </p>
    <div v-for="(value, key) in challengeOutput2" :key="key">
      <h3 v-if="value.output.length !== 0" class="h4">{{value.title}}</h3>
      <ul>
        <li v-for="item in value.output" :key="item" v-html="item"></li>
      </ul>
    </div>
    <p class="mt-4">
      The Hyland platform supports all those recommendations, and so much more. As {{ roleContent[currentRole][0] }}, you’ll appreciate that Hyland solutions are <span v-html="roleContent[currentRole][1]"></span>.
    </p>
    <img
      class="horizontal-rule"
      src="../../assets/page-12/solution-builder-page-12-dividers-02.svg"
      alt=""
    />
    <h3 class="text-blue">Where do you rank among your peers?</h3>
    <p>
      You indicated that you believe your organization is
      {{ maturityContent[currentMaturity][0] }} in its digital transformation
      journey. It’s wonderful that you’re taking initiative to learn about
      helping your workplace become more digitally advanced. Some research shows
      that you might have just one or two years left to make significant
      progress with digital transformation before your organization suffers
      financially and competitively.
    </p>
      <div class="grid-2-3">
      <div>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" class="progress-svg"><!--! Font Awesome Pro 6.1.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. -->
        <defs>
          <clipPath id="arrow">
              <path d="M310.6 182.6c-12.51 12.51-32.76 12.49-45.25 0L192 109.3V480c0 17.69-14.31 32-32 32s-32-14.31-32-32V109.3L54.63 182.6c-12.5 12.5-32.75 12.5-45.25 0s-12.5-32.75 0-45.25l128-128c12.5-12.5 32.75-12.5 45.25 0l128 128C323.1 149.9 323.1 170.1 310.6 182.6z"/>
          </clipPath>
        </defs>
          <rect x="0" y="0" fill="rgb(217,217,217)" width="100%" height="100%" clip-path="url(#arrow)" />
          <rect x="0" :y="maturityContent[currentMaturity][1]" fill="#69bf4a" width="100%" height="100%" clip-path="url(#arrow)" />
        </svg>
        <p class="h3 text-green text-center">Behind {{ maturityContent[currentMaturity][1] }} </p>
      </div>
      <div>
        <h3 class="text-gray-700" v-if="maturityContent[currentMaturity][1]">Based on our data, you might be <span class="text-green" style="font-weight: 700;">lagging behind
        {{ maturityContent[currentMaturity][1] }}</span> of your peer
        organizations.</h3>
        <p>A <a href="https://www.hyland.com/en/learn/it-programs/forrester-content-services" target="_blank">study commissioned by Forrester Consulting</a> found organizations with a strong content services strategy in place recovered more quickly and effectively from pandemic disruptions than their peers.</p>
        <p><a href="https://www.hyland.com/en/learn/it-programs/forrester-content-services" class="vanilla-module ATM01-button ATM01-button--primary ATM01-button--medium" target="_blank">Read More <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><!--! Font Awesome Pro 6.2.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2022 Fonticons, Inc. --><path d="M470.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L402.7 256 265.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160zm-352 160l160-160c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L210.7 256 73.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0z"/></svg></a></p>
      </div>
      </div>
    <img
      class="horizontal-rule"
      src="../../assets/page-12/solution-builder-page-12-dividers-03.svg"
      alt=""
    />
    <h3 class="text-blue">
      More about {{ industryContent[currentIndustry][1] }}
    </h3>
    <div v-html="industryContent[currentIndustry][2]"></div>
    <a
      class="card card-gray card-horizontal mb-4"
      :href="industryContent[currentIndustry][3]"
      target="_blank"
    >
      <div class="grid-2-3">
        <sb-image :src="industryContent[currentIndustry][4]" alt="" />
        <p style="font-size: 1.5rem;">
          {{ industryContent[currentIndustry][5] }}
        </p>
      </div>
    </a>
    <hr class="my-6">
    <EmailForm userAnswers="userAnswers" />
  </div>
</template>

<script>
import SbImage from "../SbImage/SbImage";
import EmailForm from "../Form/EmailForm";

export default {
  name: "Results",
  components: {
    SbImage,
    EmailForm
  },
  data() {
    return {
      selectedValues: this.$store.state.selectedValues,
      // content for the page
      challengeContent: {
        modernizing: [`digitally transforming your organization`],
        supporting: [`better supporting your customers`],
        businessCriticalInformation: [
          `increasing access to business-critical information`,
        ],
        transitioning: [`prioritizing your remote workforce`],
        costAndInefficiences: [`improving process efficiency`],
        informationSecurityAndCompliance: [
          `becoming more secure and compliant`,
        ],
        contentManagement: [`simplifing your tech stack`],
        legacySystems: [`modernizing your systems `],
        itResources: [`scaling your IT resources`],
        employee: [`creating a more employee-friendly workplace`],
        technologyBudget: [`managing your technology budget`],
      },
      industryContent: {
        healthcare: [
          `3,700 healthcare customers`,
          `Hyland solutions for healthcare`,
          `<p>As a healthcare organization, nothing matters more to you than your patients. Everything you do is geared towards ensuring you deliver the best care and patient experience possible. Hyland provides connected healthcare solutions that make patient content and images readily accessible, allowing your care teams to focus on their patients rather than searching for the information they need to make vital clinical decisions.</p>
          <p>Hyland Healthcare is the only provider that brings together content services and enterprise imaging into a truly connected healthcare platform.</p>`,
          `https://www.hyland.com/en/learn/healthcare/connected-healthcare-platform-ebook`,
          `/page-12/asset-thumbnails/connected-hc-platform-ebook-thumbnail.png`,
          `Learn more about why that matters.`,
          `healthcare organization`,
          `healthcare organization`,
        ],
        higherEducation: [
          `1,040 education customers`,
          `Hyland solutions for higher education`,
          `<p>As a higher education institution, you’re always looking to enhance the student, alumni, donor and other stakeholders’ experience. That’s why today, just going paperless isn’t enough. Your content needs to be streamlined and have a clean, clear process.</p>
          <p>Hyland’s market-leading suite of content services solutions enables institutions like yours to leverage the data in your current technology stacks to give staff a complete view of the student; then, they can focus on high-value engagement efforts that will further enhance the experience of your campus community.</p>`,
          `https://www.hyland.com/en/learn/higher-education/digital-transformation-ebook`,
          `/page-12/asset-thumbnails/higher-ed-digital-transformation-ebook-thumbnail.png`,
          `Learn how you can unlock the value of content services.`,
          `institution`,
          `institution`,
        ],
        insurance: [
          `535 insurance customers`,
          `Hyland solutions for insurance`,
          `<p>What if you could make better underwriting decisions? Process insured claims faster? Help your customers get back on their feet, back on the job and back to everyday life sooner than they ever imagined? Hyland insurance software solutions complement your core business systems, providing employees with a complete view of the information they need, when and where they need it.</p>`,
          `https://www.hyland.com/en/learn/insurance/complete-view-ebook`,
          `/page-12/asset-thumbnails/ins-complete-view-ebook-thumbnail.png`,
          `Learn how a complete view of your customer can strengthen relationships.`,
          `insurance company`,
          `insurance company`,
        ],
        financialServices: [
          `3,300 financial services customers`,
          `Hyland solutions for financial services`,
          `<p>In today’s digital-first world, financial services organizations are facing new types of competition and consumers that expect fast and easy digital connections. Hyland’s content services platform can help your financial institution drive digital transformation strategies, meet ever-changing compliance requirements and deliver outstanding customer experiences. With scalable and configurable capabilities, our solutions can help you chart a course for a successful digital journey.</p>`,
          `https://www.hyland.com/en/learn/financial-services/ppc/three-paths-to-accelerate-financial-services-growth`,
          `/page-12/asset-thumbnails/fins-accelerating-growth-ebook-thumbnail.png`,
          `Three paths to accelerating your digital growth`,
          `financial services organization`,
          `financial services organization`,
        ],
        government: [
          `1,815 government customers`,
          `Hyland solutions for government`,
          `<p>There has never been a greater need for technology to keep government functioning and ensure the delivery of mission-critical services without disruption. Time-consuming, paper-based and email/spreadsheet processes make it difficult for agencies and departments to efficiently deliver services and timely responses.</p>
          <p>Hyland’s content services platform can help you speed up processes and increase efficiencies while improving transparency and public trust. The platform’s scalable, configurable capabilities help each level of government meet constituent needs while laying the foundation for a streamlined, digital government.</p>`,
          `https://www.hyland.com/en/learn/government/ebook-navigating-3-key-priorities-for-state-and-local-government-transformation`,
          `/page-12/asset-thumbnails/gov-navigating-priorities-ebook-thumbnail.png`,
          `Learn which three key priorities you should focus on.`,
          `goverment agency or department`,
          `goverment agency or department`,
        ],
        manufacturing: [
          `half of 2020 Fortune 100 companies`,
          `Hyland solutions for manufacturing`,
          `<p>As a manufacturing organization, you manage a complex system from operations to quality control to supply chain, HR and more. That’s why it’s important to extend the technological innovation you’ve implemented on the manufacturing floor to your entire organization’s processes.</p>
          <p>Hyland’s platform enables manufacturers to meet the needs of each department—including accounting, finance, HR and legal—by managing content in a central repository for more streamlined operations from anywhere, on any device.</p>`,
          `https://www.hyland.com/en/learn/commercial/IDC-manufacturing-wp`,
          `/page-12/asset-thumbnails/idc-whitepaper-2019-thumbnail.png`,
          `Learn what IDC says about manufacturing in the digital age.`,
          `manufacturing organization`,
          `manufacturing organization`,
        ],
        other: [
          `half of 2020 Fortune 100 companies`,
          `the Hyland platform`,
          `<p>Hyland is focused on empowering organizations with the most flexible, extensible and configurable cloud content services platform on the planet. We’re ready to take your customer and employee experience, digital transformation, data security and compliance to the next level.</p> 
          <p>We’re not the solution—you and your employees are. We simply hand you the tools you need to create exceptional outcomes tailored to your needs and industry. Some benefits of our solutions include:</p>
          <ul>
            <li>Intelligent automation so your team can focus on high-value work</li>
            <li>Agile and adaptive to support your evolving requirements</li>
            <li>Low-code and configurable without a developer’s background</li>
            <li>Designed to streamline workflows and business processes</li>
          </ul>`,
          `https://www.hyland.com/-/media/Project/Hyland/Common/pdfs/ebook/prod-ebook-hyland-101`,
          `/page-12/asset-thumbnails/discover-hyland-ebook-thumbnail.png`,
          `Discover more about our content services platform in this ebook.`,
          `organization`,
          `organization`,
        ],
      },
      roleContent: {
        decisionMaker: [
          `a decision-maker`,
          `<a href="https://www.hyland.com/en/resources/analyst-coverage" target="_blank">consistently top-rated by the leading industry analysts</a>, not only for the innovative solutions themselves but our quality customer service and strong satisfied customer base. From content capture, automation, collaboration, retention and more, Hyland offers scalable solutions to grow with your business and needs`,
        ],
        manager: [
          `a manager`,
          `easy to use from any device and secure from anywhere in the world so your employees will have access to all the information they need across your organization. Plus, our customer success representatives are friendly and responsive (just in case you ever need help)`,
        ],
        developer: [
          `a developer`,
          `scalable to grow with your business, all in a low-code content services platform that minimizes risk and allows you to quickly respond to business needs while focusing your time on more high-value work. Plus, our friendly and responsive customer success representatives are always ready to help if you need`,
        ],
        endUser: [
          `an end user`,
          `easy to use from any device and secure from anywhere in the world, so you can access the information you need, when you need it. Plus, our customer success representatives are friendly and responsive (just in case you ever need help)`,
        ],
        systemsTrainer: [
          `a systems trainer`,
          `easy to use from any device and secure from anywhere in the world. The platform can be configured to meet unique user needs and permission levels. Plus, you'll have the support of our friendly customer success representatives and the Hyland Community — an online web portal with a wealth of product, solution and industry resources`,
        ],
        other: [
          `a valuable employee`,
          `easy to use from any device and secure from anywhere in the world, so you can access the information you need, when you need it. Plus, our customer success representatives are friendly and responsive (just in case you ever need help)`,
        ],
      },
      maturityContent: {
        0: [`very early`, `90%`],
        1: [`somewhat early`, `74%`],
        2: [`somewhat early`, `48%`],
        3: [`somewhat early`, `22%`],
        4: [`far along`, ``],
      },
      currentIndustry: "other",
      currentChallenge: "default",
      currentMaturity: 0,
      currentRole: "other",
      currentSize: 50,
    };
  },
  computed: {
    challengeOutput() {
      let val = [];
      Object.values(this.selectedValues).forEach((value) => {
        if (value.isSelected === true && value.category === "challenges") {
          val.push(value.value);
        }
      });

      let newString;

      // For first challenge Output
      switch (val.length) {
        case 0:
          newString = this.challengeContent.modernizing[0]; // Default value
          break;
        case 1:
          newString = this.challengeContent[val[0]][0];
          break;
        case 2:
          newString = `${this.challengeContent[val[0]][0]} and ${
            this.challengeContent[val[1]][0]
          }`;
          break;
        case 3:
          newString = `${this.challengeContent[val[0]][0]}, ${
            this.challengeContent[val[1]][0]
          } and ${this.challengeContent[val[2]][0]}`;
          break;
      }
      return newString;
    },
    challengeOutput2() {
      let challengesSelected = {};

      // Collects values of user selection for challenges
      Object.values(this.selectedValues).forEach((value) => {
        console.log(`${value.category}:`, value)
        if (value.isSelected === true && value.category === "challenges") {
          challengesSelected[value.value] = { title: value.title || value.output || value.alt };
          // creates {value1: output1, value2: output2, value3: output3}
        }
      });

      if (Object.keys(challengesSelected).length <= 0) {
        // default value
        challengesSelected["modernizing"] = { title: "Modernize or digitally transform your operations" };
      }

      let setup = {
        modernizing: [1, 2, 3],
        supporting: [5, 6, 7],
        businessCriticalInformation: [4, 8, 9],
        transitioning: [3, 10],
        costAndInefficiences: [2, 11],
        informationSecurityAndCompliance: [3, 12, 13],
        contentManagement: [4, 9, 14],
        legacySystems: [2, 14, 15],
        itResources: [4, 14],
        employee: [10, 11],
        technologyBudget: [9, 14, 15],
      };

      let output = [
        '<strong>Develop or revisit your digital transformation strategy:</strong> Your <a href="https://www.hyland.com/en/insights/grow-into-the-future" target="_blank">digital transformation</a> should be strategic, not reactionary. Before you take any steps toward modernizing or digitizing your operations, you’ll need a true digital transformation strategy. If you have an existing strategy, revisit it regularly to ensure it still aligns with your vision of where you want to be and how you’re going to get there.',
        '<strong>Explore Intelligent automation (IA):</strong> <a href="https://www.hyland.com/en/insights/grow-smarter" target="_blank">Intelligent automation</a> can help your organization manage its processes and data more efficiently, anticipate the needs of users and customers, and give your employees the opportunity to focus on high-value work. By combining technologies like workflow automation, business logic, machine learning and analytics, AI is a driving force behind digital transformation that eliminates the cost and frustration often associated with outdated legacy systems.',
        '<strong>Move toward cloud-delivered and SaaS solutions:</strong> A remote workforce, growing information security risks and ever-changing compliance regulations are difficult to manage when your data is kept on-prem. <a href="https://www.hyland.com/en/innovation/hyland-cloud" target="_blank">Cloud-delivered solutions</a> provide the most agile, secure, scalable access to critical information across your organization. And as the world changes around your organization, the cloud helps you adapt quickly by leveraging new applications, services and features as soon as they’re available. In short: Operating in the cloud leads to better, more reliable business outcomes.',
        '<strong>Look into low-code development solutions:</strong> When your organization requires a new technology solution, buying or coding are no longer your only options. a <a href="https://www.hyland.com/en/insights/grow-faster" target="_blank">low-code rapid application development platform</a> allows non-programmers to configure and deploy solutions on one platform—minimizing information silos and synchronizing information among different systems with native integration capabilities.',
        '<strong>Focus on customer experience:</strong> By shifting attention to customer relationships and prioritizing <a href="https://www.hyland.com/en/complete-view/articles/featured/customer-experience" target="_blank">customer experience strategy</a>, especially in times when budgets are tight and human connections are vital (albeit virtual), your organization can distinguish itself as one that prioritizes its customers in the best and worst of times. To do so, you need to free your employees – in all roles and departments – from the manual, mundane tasks that make it difficult for them to spend time on your customers.',
        '<strong>Consider a customer communications management (CCM) solution:</strong> With <a href="https://www.hyland.com/en/platform/capabilities-and-features/customer-communications-management" target="_blank">CCM technology</a>, organizations overcome the inherent obstacles of manually assembling and outputting frequently created documents. By integrating with enterprise applications and office productivity software, CCM solutions reduce the time and effort it takes to create individually personalized content and improve customer communication, satisfaction, acquisition and retention.',
        '<strong>Get familiar with case management:</strong> <a href="https://www.hyland.com/en/platform/capabilities-and-features/case-management" target="_blank">Case management solutions</a> empower employees to make informed decisions by giving them a complete view of all the information they need — in context and on a single platform. By increasing productivity, ensuring process continuity and driving value with fast deployment and quick returns, connected case management solutions scale to evolve with your growing customer service and business needs and can ultimately provide better customer service.',
        '<strong>Start vetting content services vendors:</strong> A flexible, <a href="https://www.hyland.com/en/explore/gartner-magic-quadrant-for-content-services-platforms" target="_blank">cloud-based content services solution</a> lets you organize, manage and optimize critical content across your organization, so its users have a complete view of the information they need, when they need it. Being able to easily search and track content, effectively handle revisions and access the newest versions of information at any time means your employees can be more efficient, effective and collaborative.',
        '<strong>Integrate your critical systems:</strong> Your organization uses multiple critical applications every day to drive its core business processes – but if you’re not <a href="https://www.hyland.com/en/solutions/department-integrations" target="_blank">integrating them with a content services solution</a>, you’re missing out on a lot of value. A platform that connects your data and systems enterprise-wide means users can access to complete, up-to-date information directly from the core applications they use every day. This means less manual data entry, reduced training requirements, instant access to related content across systems, and more.',
        '<strong>Ensure a user-centric platform and solutions:</strong> Your <a href="https://www.hyland.com/en/platform/benefits/user-experience" target="_blank">content services platform</a> should be designed for the optimal user experience. It’s no use having a system that people can’t — or won’t — use. It’s crucial to choose a platform that’s intuitive, configurable, responsive and standards-based, versatile and facilitates collaboration. Look for one that works how your employees work, no matter where they are, so they remain happy and productive.',
        '<strong>Start automating with robotic process automation (RPA):</strong> <a href="https://www.hyland.com/en/insights/trending-topics/robotic-process-automation" target="_blank">RPA</a> is an end-to-end suite of tools designed to automate, optimize and transform your business processes with intelligent automation. It can capture and organize unstructured information and data to increase efficiency and accuracy, while improving employee satisfaction and retention by reducing or eliminating repetitive and boring tasks.',
        '<strong>Rely on a retention and records management solution:</strong> <a href="https://www.hyland.com/en/platform/capabilities-and-features/records-management-and-retention" target="_blank">Retention and records management solutions</a> allow you to automate the lifecycle of your content for enhanced operational efficiencies that help support security and compliance requirements. Optimal product functionality includes multiple storage, archival and destruction options based on predefined schedules.',
        '<strong>Trust a tool to support security and compliance requirements:</strong> Threats to your data increase every day. So does the complexity of the regulatory environment. You need automated <a href="https://www.hyland.com/en/platform/benefits/data-security" target="_blank">security and compliance</a> tools that keep track of your information through its entire lifecycle. Look for solutions that take security and compliance seriously by prioritizing data classification, data masking and redaction, encryption, role-based access controls, redundant configurations and more.',
        '<strong>Look for a connected and extensible content services platform:</strong> A robust <a href="https://www.hyland.com/en/platform" target="_blank">content services platform</a> puts powerful capabilities at your disposal to simplify and support the way you work. Intuitive configuration controls and low-code rapid application development give you the flexibility to scale as you grow and deploy quickly to address business needs. Whether users interact with the platform directly or leverage its technology through other applications, it should give you the tools to build the solutions you need, all in a centralized interface.',
        '<strong>Switch to a modern platform:</strong> The legacy ECM systems of the past can’t support the processes of today. It’s time to <a href="https://www.hyland.com/en/platform/switching-to-hyland" target="_blank">make the switch to an industry-leading content services platform</a>. Your organization needs cloud deployment options, low code development, microservices and APIs to provide the infrastructure needed for next-generation application delivery.',
      ];


      let compareArray = [];

      Object.keys(challengesSelected).forEach((key) => {
        challengesSelected[key].output = [];
        compareArray.push(setup[key]);
      });

      let [a = [], b = [], c = []] = compareArray;
      let outputArray = [];

      // [4, 14] [4, 9, 14]
      // match array value to each value in other array
      // if match then remove from longest array
      // do so for however many arrays there are

      if (c.length !== 0) {
      c.forEach(value => {
        if (a.includes(value)) {
          if (a.length >= c.length) {
            a.splice(a.indexOf(value), 1)
          } else {
            c.splice(c.indexOf(value), 1)
          }
        }
      })
      outputArray.push(c);
      }

      if (b.length !== 0) {
      b.forEach(value => {
        if (c.includes(value)) {
          if (c.length >= b.length) {
            c.splice(c.indexOf(value), 1)
          } else {
            b.splice(b.indexOf(value), 1)
          }
        }
      })
      outputArray.push(b);
      }

      if (a.length !== 0) {
      a.forEach(value => {
        if (b.includes(value)) {
          if (a.length > b.length) {
            a.splice(a.indexOf(value), 1)
          } else {
            b.splice(b.indexOf(value), 1)
          }
        }
      })
      outputArray.push(a);
      }

      outputArray = outputArray.reverse();

      console.log('outputArray: ', outputArray);

       Object.keys(challengesSelected).forEach((key, index) => {
        outputArray[index].forEach((value) => {
          challengesSelected[key].output.push(output[value - 1])
        })
      });

      return challengesSelected;
    },
  },
  methods: {
    collect(category) {
      let val;
      Object.values(this.selectedValues).forEach((value) => {
        if (value.isSelected === true && value.category === category) {
          val = value.value;
        }
      });
      return val;
    },
    init() {
      this.currentIndustry = this.collect("industry") || "other";
      this.currentRole = this.collect("role") || "other";
      this.currentMaturity = this.$store.state.selectedValues.maturity;
      this.currentSize = this.$store.state.selectedValues.size;
    },
    fallbackToDefault() {
      let arr = [];

      for (let i = 0; i < this.industryContent["other"].length; i++) {
        if (this.industryContent[this.currentIndustry][i]) {
          arr.push(this.industryContent[this.currentIndustry][i]);
        } else {
          arr.push(this.industryContent["other"][i]);
        }
      }

      this.industryContent[this.currentIndustry] = arr;
    },
  },
  mounted() {
    this.init();
    this.fallbackToDefault();
  },
};
</script>

<style scoped>
@media (min-width: 576px) {
  #results {
    padding-left: 6rem;
    padding-right: 6rem;
  }
}

img.horizontal-rule {
  margin-left: -6rem;
  margin-right: -6rem;
  margin-top: 2rem;
  max-width: none;
}

.grid-2-3 {
  display: grid;
  grid-template-columns: 1fr 2fr;
  align-items: center;
  grid-gap: 15px;
}

h4 {
  font-weight: 200;
}

.card-horizontal {
  padding-top: 1rem;
  padding-left: 1rem;
  border: 0;
  margin-top: 2rem;
}

/*-- progress bar --*/
.progress {
  width: 150px;
  height: 150px;
  line-height: 150px;
  margin: 0 auto;
  position: relative;
}
.progress:after {
  content: "";
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 15px solid #f2f5f5;
  position: absolute;
  top: 0;
  left: 0;
  box-sizing: border-box;
}
.progress > span{
  width: 50%;
  height: 100%;
  overflow: hidden;
  position: absolute;
  top: 0;
  z-index: 1;
}
.progress .progress-left{
  left: 0;
}
.progress .progress-bar{
  width: 100%;
  height: 100%;
  background: none;
  border-width: 15px;
  border-style: solid;
  position: absolute;
  top: 0;
}
.progress .progress-left .progress-bar {
  left: 100%;
  border-top-right-radius: 80px;
  border-bottom-right-radius: 80px;
  border-left: 0;
  -webkit-transform-origin: center left;
  transform-origin: center left;
}
.progress .progress-right {
  right: 0;
}
.progress .progress-right .progress-bar {
  left: -100%;
  border-top-left-radius: 80px;
  border-bottom-left-radius: 80px;
  border-right: 0;
  -webkit-transform-origin: center right;
  transform-origin: center right;
}
.progress .progress-value {
  width: 100%;
  height: 100%;
  font-size: 24px;
  text-align: center;
  position: absolute;
  color: #26abfd;
}

.progress .progress-value svg {
  width: 15px;
  height: 20px;
  fill: #26abfd;
  position: relative;
  top: 2px;
}

.progress.blue .progress-bar {
  border-color: #26abfd;
}

.progress-svg {
  width: 100px;
  height: 160px;
  display: block;
  margin: 1rem auto;
}

.ATM01-button svg {
  height: 12px;
}

ul {
  list-style: inside;
  list-style-type: disc;
  font-size: 1.8rem;
  line-height: 180%;
  margin-left: 24px;
}

li {
  font-size: 1.8rem;
  line-height: 180%;
  margin-top: 8px;
}

li:last-child {
  margin-bottom: 16px;
}

</style>
